import * as Yup from 'yup';
import cron from 'cron-validate';
import { backupScheduleCronOptions, enterpriseCronOptions } from 'app/constants/cron';
import { durationUnits, MemoryEnum, RightType } from '../constants';
import { EnvVar, StorageDto, ValidatorDbConfigDto } from '../openapi';

// Regexp ----------------------------
export const nameRegExp = /^[a-z]([a-z0-9\\-]*[a-z0-9])?$/;
export const partyNameRegExp = /^[a-z]([a-z0-9.\\-]*[a-z0-9])?$/;
export const nodeIdentifierRegExp = /^[a-zA-z0-9.-]+$/;
export const urlRegExp =
  /^((https?|ftp):\/\/)?([^\s:@]+(:[^\s:@]*)?@)?([^\s:@]+)(:[0-9]+)?(\/[^\s]*)?(\?[^\s#]*)?(#[^\s]*)?$/;
export const imageTagRegExp = /^(\d+)\.(\d+)(\.\d+)?$/;
export const noUpperCaseRegExp = /^[^A-Z]*$/;
export const userNameRegExp = /^[a-z][a-z0-9\\-]*$/;
export const userIdRegExp = /^[a-zA-Z0-9@^$.!`\-#+'~_|:]{1,128}$/;
export const partyHintRegExp = /^[a-zA-Z0-9]+-[a-zA-Z0-9]+-\d+$/;
export const customAuthRegExp = /^[\w\S]+$/;
export const cantonImageRegExp = /^[a-zA-Z0-9\\.\\:\\/\\-]+$/;
export const contactPointRegExp = /^[\w\S]+$/;

const envVarRegExp = /^[^\s][\x20-\x7E\x80-\xFF]*$/;
// https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/#meaning-of-cpu

const cpuResourcesRegExp = /^[0-9]+m$|^[0-9]+\.([0-9]{1,3})$|^[0-9]+$/;

const milliCpuRegExp = /^[0-9]+m$/;

// https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/#meaning-of-memory

//  Helpers  ----------------------------
const unitSize = {
  [MemoryEnum.Ei]: 2 ** 60,
  [MemoryEnum.Pi]: 2 ** 50,
  [MemoryEnum.Ti]: 2 ** 40,
  [MemoryEnum.Gi]: 2 ** 30,
  [MemoryEnum.Mi]: 2 ** 20,
  [MemoryEnum.Ki]: 2 ** 10,
};

// Messages  ----------------------------
export const VALUE_NOT_VALID_ERROR_MESSAGE = 'Value is not valid.';
export const VALUE_NOT_EMPTY_ERROR_MESSAGE = 'Value should be not empty.';
export const VALUE_INTEGER_MESSAGE = 'Value should be integer.';
export const INTEGER_MIN_MESSAGE = 'Value should be greater than or equal to';
// const INTEGER_MAX_MESSAGE = 'Value should be less than or equal to';
export const FIELD_REQUIRED_ERROR_MESSAGE = 'Field is required.';
export const INVALID_FORMAT_MESSAGE = 'Invalid format.';
export const INVALID_URL_MESSAGE = 'Invalid url.';
export const DEFAULT_ALPHANUMERIC_VALIDATION_MESSAGE = `It should contain a lower case alphanumeric characters (a-z) or '-', and must start with an alphanumeric character.`;
export const APP_NAME_VALIDATION_MESSAGE = `It must contain lowercase alphanumeric characters (a–z) or "-" and must start with a letter.`;
export const VALIDATION_FIELD_DEFAULT_MESSAGE = `${VALUE_NOT_VALID_ERROR_MESSAGE} ${DEFAULT_ALPHANUMERIC_VALIDATION_MESSAGE}`;
export const PORT_VALIDATION_MESSAGE = 'Port value must be a number. E.g. 8080';

// Schemas  ----------------------------
export const envVarsArrSchema = Yup.array()
  .of(
    Yup.object({
      name: Yup.string().matches(envVarRegExp, 'Variable name is not valid').required('Variable name is required'),
      value: Yup.string().matches(envVarRegExp, 'Variable value is not valid'),
      valueFrom: Yup.object()
        .shape({
          secretKeyRef: Yup.object().shape({
            name: Yup.string()
              .test('secret value validation', 'Source value is required', (_, context) => !!context.originalValue)
              .matches(envVarRegExp, 'Variable secret name is not valid'),
            key: Yup.string()
              .test('secret key validation', 'Source key is required', (_, context) => !!context.originalValue)
              .matches(envVarRegExp, 'Variable secret key is not valid'),
            optional: Yup.boolean(),
          }),
        })
        .nullable(),
    }),
  )
  .test('unique names validation', 'Duplicate variable names are not allowed', (values, context) => {
    if (!values) return true;
    const envNames = values?.map((env) => env.name) || [];
    const postgresEnvNames = context.parent?.postgresEnvVars?.map((env) => env.name) || [];

    const resEnvVars = postgresEnvNames.length > 0 ? values?.concat(context.parent.postgresEnvVars) : values;
    const resEnvNames = postgresEnvNames.length > 0 ? [...envNames, ...postgresEnvNames] : envNames;

    return resEnvVars?.length === new Set(resEnvNames).size;
  });

export const envVarsSchema: Yup.ObjectSchema<{ envVars?: EnvVar[] }> = Yup.object().shape({
  envVars: envVarsArrSchema,
});

const memorySchemaFn = (msg: string) =>
  Yup.object().shape({
    value: Yup.string()
      .required(FIELD_REQUIRED_ERROR_MESSAGE)
      .test('memory validation for requested value', msg, (_, context) => {
        const limitSize =
          parseFloat(context?.from?.[1]?.value.memoryLimit?.value) *
          unitSize[context?.from?.[1]?.value.memoryLimit?.unit];
        const requestSize =
          parseFloat(context?.from?.[1]?.value.memoryRequested?.value) *
          unitSize[context?.from?.[1]?.value.memoryRequested?.unit];

        return limitSize >= requestSize;
      }),
  });

const cpuSchemaFn = (msg: string) =>
  Yup.string()
    .required(FIELD_REQUIRED_ERROR_MESSAGE)
    .matches(cpuResourcesRegExp, INVALID_FORMAT_MESSAGE)
    .test('CPU validation for requested value', msg, (_, context) => {
      const cpuRequested = context?.from?.[1].value.resources.cpuRequested;
      const cpuLimit = context?.from?.[1].value.resources.cpuLimit;
      const requestSize = milliCpuRegExp.test(cpuRequested)
        ? +cpuRequested.replace('m', '')
        : parseFloat(cpuRequested) * 1000;
      const limitSize = milliCpuRegExp.test(cpuLimit) ? +cpuLimit.replace('m', '') : parseFloat(cpuLimit) * 1000;

      return requestSize <= limitSize;
    });

export const getNameValidation = (maxLength = 30) => {
  return Yup.string()
    .max(maxLength, `Value should contain less than or equal to ${maxLength} characters`)
    .matches(nameRegExp, `${VALIDATION_FIELD_DEFAULT_MESSAGE}`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE);
};

const resourcesSchemaBase = {
  cpuLimit: cpuSchemaFn('CPU limit should be more or equal then Requested CPU'),
  cpuRequested: cpuSchemaFn('Requested CPU should be less or equal then CPU limit'),
  memoryRequested: memorySchemaFn('Requested memory should be less or equal then memory limit'),
  memoryLimit: memorySchemaFn('Memory limit should be more or equal then requested memory'),
};

export const resourcesSchema = Yup.object().shape({
  ...resourcesSchemaBase,
  replicas: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(1, `${INTEGER_MIN_MESSAGE} 1.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
});

export const validatorResourcesSchema = Yup.object().shape({
  ...resourcesSchemaBase,
  replicas: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(0, `${INTEGER_MIN_MESSAGE} 0.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
});

const sizeValidationSchema = Yup.object()
  .shape({
    value: Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
    unit: Yup.mixed<MemoryEnum>().oneOf(Object.values(MemoryEnum)).required(),
  })
  .required(FIELD_REQUIRED_ERROR_MESSAGE);

export const storageSchema = Yup.object().shape({
  type: Yup.mixed<StorageDto.type>().oneOf(Object.values(StorageDto.type)).required(FIELD_REQUIRED_ERROR_MESSAGE),
  user: Yup.string().test(VALUE_NOT_EMPTY_ERROR_MESSAGE, FIELD_REQUIRED_ERROR_MESSAGE, (value, context) => {
    if ((context.parent as StorageDto).type !== StorageDto.type.MEMORY && !value) return false;
    return true;
  }),
  password: Yup.string().test(VALUE_NOT_EMPTY_ERROR_MESSAGE, FIELD_REQUIRED_ERROR_MESSAGE, (value, context) => {
    if ((context.parent as StorageDto).type !== StorageDto.type.MEMORY && !value) return false;
    return true;
  }),
  hostname: Yup.string().test(VALUE_NOT_EMPTY_ERROR_MESSAGE, FIELD_REQUIRED_ERROR_MESSAGE, (value, context) => {
    if ((context.parent as StorageDto).type === StorageDto.type.EXTERNAL && !value) return false;
    return true;
  }),
  port: Yup.string().test(VALUE_NOT_EMPTY_ERROR_MESSAGE, FIELD_REQUIRED_ERROR_MESSAGE, (value, context) => {
    if ((context.parent as StorageDto).type === StorageDto.type.EXTERNAL && !value) return false;
    return true;
  }),
  // during validation the size should be the next format and when we send it to
  // server change to string
  size: Yup.object().when('type', {
    is: (val: StorageDto.type) => val === StorageDto.type.POSTGRES,
    then: () => sizeValidationSchema,
  }),
  backupSize: Yup.object().when('type', {
    is: (val: StorageDto.type) => val !== StorageDto.type.MEMORY,
    then: () => sizeValidationSchema,
  }),
});

// Common database config validation fields (external, hostname, port)
export const databaseConfigBaseSchema = {
  external: Yup.boolean(),
  hostname: Yup.string().when('external', {
    is: true,
    then: () => Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
    otherwise: () => Yup.string(),
  }),
  port: Yup.string().when('external', {
    is: true,
    then: () =>
      Yup.number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && /\s/.test(originalValue)) return NaN;
          const num = Number(originalValue);
          return Number.isNaN(num) ? NaN : num;
        })
        .typeError(PORT_VALIDATION_MESSAGE)
        .min(1, `${INTEGER_MIN_MESSAGE} 1`)
        .max(65535, 'Port value should be less than or equal to 65535')
        .required(FIELD_REQUIRED_ERROR_MESSAGE),
    otherwise: () => Yup.string(),
  }),
};

export const databaseConfigSchema = Yup.object<ValidatorDbConfigDto>().shape({
  ...databaseConfigBaseSchema,
  username: Yup.string()
    .matches(userNameRegExp, VALIDATION_FIELD_DEFAULT_MESSAGE)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
  password: Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
});

export const createSchema = Yup.object().shape({
  ...envVarsSchema.fields,
  image: Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
  adminPort: Yup.number().required(FIELD_REQUIRED_ERROR_MESSAGE),
  storage: storageSchema,
  resources: resourcesSchema,
});

export const ParticipantUploadDarSchema = Yup.object().shape({
  darName: Yup.array().of(
    Yup.string().test('DAR File', 'Select the DAR file to upload.', (value, context) => {
      const file = context?.from?.[0].value.darName;
      if (!file || !value) return false;

      return true;
    }),
  ),
});

const isValid = (value) => value.split(' ').join('').length !== 0;

const rightsSchema = Yup.object().shape({
  rights: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string()
          .ensure()
          .test('Type empty?', 'Type cannot be empty', (value) => isValid(value)),
        party: Yup.string()
          .ensure()
          .test('Party empty?', 'Party cannot be empty', (value, ctx) => {
            if (
              ctx.parent.type !== RightType.ParticipantAdmin &&
              ctx.parent.type !== RightType.IdentityProviderAdmin &&
              ctx.parent.type !== RightType.CanReadAsAnyParty &&
              ctx.parent.type !== RightType.CanExecuteAsAnyParty
            ) {
              return isValid(value);
            }
            return true;
          }),
      }),
    )
    .test('has-rights-or-admin', 'Select at least one right or admin permission.', function (rights) {
      const { isParticipantAdmin, isCanReadAsAnyParty, isCanExecuteAsAnyParty, isIdentityProviderAdmin } = this.parent;

      const hasAdminRight =
        !!isParticipantAdmin || !!isCanReadAsAnyParty || !!isCanExecuteAsAnyParty || !!isIdentityProviderAdmin;

      if ((rights?.length ?? 0) > 0 || hasAdminRight) {
        return true;
      }

      return this.createError({
        path: this.path,
        message: 'Select at least one right or admin permission.',
      });
    }),
  isParticipantAdmin: Yup.boolean().nullable(),
  isCanReadAsAnyParty: Yup.boolean().nullable(),
  isCanExecuteAsAnyParty: Yup.boolean().nullable(),
  isIdentityProviderAdmin: Yup.boolean().nullable(),
});

export const UserCreateSchema = Yup.object().shape({
  userId: Yup.string().matches(userIdRegExp, INVALID_FORMAT_MESSAGE).required(FIELD_REQUIRED_ERROR_MESSAGE),
  primaryParty: Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
  ...rightsSchema.fields,
});

export const UserManageRightsSchema = Yup.object().shape({
  ...rightsSchema.fields,
});

export const DomainEditSchema = Yup.object().shape({
  image: Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
  resources: resourcesSchema,
  ...envVarsSchema.fields,
});

export const ApplicationEditSchema = Yup.object().shape({
  resources: resourcesSchema,
});

export const ParticipantConnectSchema = Yup.object().shape({
  name: Yup.string(),
  domainAlias: Yup.string().matches(userNameRegExp, INVALID_FORMAT_MESSAGE).required(FIELD_REQUIRED_ERROR_MESSAGE),
  domainName: Yup.string().when('custom', {
    is: false,
    then: () => Yup.string().required(FIELD_REQUIRED_ERROR_MESSAGE),
  }),
  domainUrl: Yup.string().when('custom', {
    is: true,
    then: () => Yup.string().matches(cantonImageRegExp, INVALID_FORMAT_MESSAGE).required(FIELD_REQUIRED_ERROR_MESSAGE),
  }),
  publicPort: Yup.string(),
  connected: Yup.boolean().nullable(),
  port: Yup.number(),
});

export const PartyCreateSchema = Yup.object().shape({
  displayName: Yup.string().matches(
    partyNameRegExp,
    `${VALUE_NOT_VALID_ERROR_MESSAGE} It should contain a lower case alphanumeric characters (a-z) or '-', or '.', and must start with an alphanumeric character.`,
  ),
});

export const ScheduleValidatorPruningSchema = Yup.object().shape({
  cron: Yup.string()
    .test('Cron valid??', 'Cron must be valid', (_, ctx) => {
      const cronOptions = {
        useSeconds: true, // enable seconds field → 6 fields
        useYears: false, // enable years field → 7 fields possible
        useAliases: true, // enables aliases for month and daysOfWeek fields
        useBlankDay: true, // allow "?" in day-of-month / day-of-week
        allowOnlyOneBlankDayField: true, // enforce Quartz rule: only one "?" at a time
        alias: true, // allow JAN, FEB, MON, etc.
        allowLastDayOfMonth: true, // support "L"
        allowLastDayOfWeek: true, // support "L" in day-of-week
        allowWeekday: true, // support "W" (weekday nearest)
        allowNthWeekdayOfMonth: true, // support "#"
        allowSeconds: true, // redundant but explicit
        allowMinutes: true,
        allowHours: true,
        allowDaysOfMonth: true,
        allowMonths: true,
        allowDaysOfWeek: true,
        allowYears: true,
        // Stepping syntax (*/5, 0/10, etc.)
        allowStepping: true,
      };

      const cronResult = cron(ctx.originalValue as string, {
        override: cronOptions,
        // preset: 'aws-cloud-watch',
      });

      if (cronResult.isValid()) {
        return true;
      }

      const errorsSet = new Set(cronResult.getError());

      return ctx.createError({
        message: Array.from(errorsSet).join(', '),
        path: ctx.path,
      });
    })
    .required(
      `${FIELD_REQUIRED_ERROR_MESSAGE} Syntax: second (0-59), minute (0-59), hour (0-23), day of the month (1-31), month (1-12, JAN to DEC), day of the week (0-6, Sunday to Saturday OR sun, mon, tue, wed, thu, fri, sat)`,
    ),
  maxDurationValue: Yup.number().min(1).required(),
  maxDurationUnit: Yup.string().oneOf(durationUnits).required(),
  retentionValue: Yup.number().min(1).required(),
  retentionUnit: Yup.string().oneOf(durationUnits).required(),
});

export const SchedulePruningSchema = Yup.object().shape({
  cron: Yup.string()
    .test('Cron valid??', 'Cron must be valid', (_, ctx) => {
      const cronOptions = ctx.parent.enterprise
        ? {
            ...enterpriseCronOptions,
            useYears: ctx.parent.year,
          }
        : {
            useAliases: true,
            useBlankDay: true,
          };

      const cronResult = cron(ctx.originalValue as string, {
        override: cronOptions,
      });

      if (cronResult.isValid()) {
        return true;
      }

      const errorsSet = new Set(cronResult.getError());

      return ctx.createError({
        message: Array.from(errorsSet).join(', '),
        path: ctx.path,
      });
    })
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
  retentionInSec: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(1, `${INTEGER_MIN_MESSAGE} 1.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
  maxDurationInSec: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(1, `${INTEGER_MIN_MESSAGE} 1.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
});

export const MediatorSchedulePruningSchema = Yup.object().shape({
  mediator: Yup.object().shape({
    ...SchedulePruningSchema.fields,
  }),
});

export const SequencerSchedulePruningSchema = Yup.object().shape({
  sequencer: Yup.object().shape({
    ...SchedulePruningSchema.fields,
  }),
});

export const ScheduleBackupSchema = Yup.object().shape({
  cron: Yup.string()
    .test('Is cron valid?', 'Cron must be valid', (_, ctx) => {
      const cronResult = cron(ctx.originalValue as string, {
        override: backupScheduleCronOptions,
      });

      if (cronResult.isValid()) {
        return true;
      }

      const errorsSet = new Set(cronResult.getError());

      return ctx.createError({
        message: Array.from(errorsSet).join(', '),
        path: ctx.path,
      });
    })
    .required(
      `${FIELD_REQUIRED_ERROR_MESSAGE} Syntax: minute (0-59), hour (0-23), day of the month (1-31), month (1-12, JAN to DEC), day of the week (0-6, Sunday to Saturday OR sun, mon, tue, wed, thu, fri, sat)`,
    ),
  maxBackups: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(1, `${INTEGER_MIN_MESSAGE} 1.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
});

export const ScheduleIdentityDumpSchema = Yup.object().shape({
  cron: Yup.string()
    .test('Is cron valid?', 'Cron must be valid', (_, ctx) => {
      const cronResult = cron(ctx.originalValue as string, {
        override: backupScheduleCronOptions,
      });

      if (cronResult.isValid()) {
        return true;
      }

      const errorsSet = new Set(cronResult.getError());

      return ctx.createError({
        message: Array.from(errorsSet).join(', '),
        path: ctx.path,
      });
    })
    .required(
      `${FIELD_REQUIRED_ERROR_MESSAGE} Syntax: minute (0-59), hour (0-23), day of the month (1-31), month (1-12, JAN to DEC), day of the week (0-6, Sunday to Saturday OR sun, mon, tue, wed, thu, fri, sat)`,
    ),
  maxDumps: Yup.number()
    .integer(VALUE_INTEGER_MESSAGE)
    .min(1, `${INTEGER_MIN_MESSAGE} 1.`)
    .required(FIELD_REQUIRED_ERROR_MESSAGE),
});

export const uploadFileSchema = Yup.object({
  files: Yup.array().required(FIELD_REQUIRED_ERROR_MESSAGE),
});
